import type { Maybe } from 'graphql/jsutils/Maybe';
import type { DeselectOptionActionMeta, SelectOptionActionMeta } from 'react-select';

import type { AdType } from '../../configs';
import { AD_TYPE_PUBLISHER_RESTRICTIONS_MAP } from '../../configs';
import type { Nullable, TargetingTermValue } from '../../models';
import type { TargetingValueOption } from '../AdForm/adFormik';
import type { DropdownOption } from '../Dropdown';
import { ActionTypes, isClearAllOption } from '../MultiSelectDropdown';
import { CustomDimension, DIMENSIONS_TO_DISABLE, GeoDimension, LINE_ITEM_DIMENSION_EXCLUSION_LIST } from './constants';
import type {
  DisableTargetingActionsParams,
  MultiSelectTargetingValue,
  SupportedDimensionOptions,
  SupportedValuesOptions,
} from './types';

export const getEmptyTextMessage = (loading: boolean, requiredEntry: Nullable<DropdownOption>): string => {
  if (!requiredEntry) return 'Select a category and type first';

  return loading ? 'Loading...' : 'Select';
};

export const formatOptionsToDropdownOptions = (options: MultiSelectTargetingValue): DropdownOption[] =>
  options.map((option) => ({
    isDisabled: false,
    description: undefined,
    ...option,
  }));

export const formatOptionsToTargetingValueOptions = (
  options: MultiSelectTargetingValue,
  selectedDimension: DropdownOption,
  selectedValues: TargetingValueOption[]
): TargetingValueOption[] =>
  selectedDimension?.id
    ? formatOptionsToDropdownOptions(options).map((option) => ({
        ...option,
        dimension: selectedDimension.id,
      }))
    : selectedValues;

export const getIndicesOfUnselectedValuesToRemove = (
  targetings: Pick<TargetingTermValue, 'dimension' | 'value'>[],
  selectedDimension: Nullable<DropdownOption>,
  selectedValues: TargetingValueOption[]
): number[] => {
  if (!selectedDimension) return [];
  const selectedSet = new Set(selectedValues.map(({ id, value }) => `${id}:${value}`));
  const indices: number[] = [];
  for (let i = targetings.length - 1; i >= 0; i--) {
    // collect relevant indices in reverse order to simplify removal
    const { dimension, value } = targetings[i];
    if (dimension.id === selectedDimension.id && !selectedSet.has(`${value?.id}:${value?.name}`)) {
      indices.push(i);
    }
  }
  return indices;
};

export const getCurrentSelectedValues = (
  currentSelected: DropdownOption[],
  metas: Array<SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>>
): DropdownOption[] =>
  metas.reduce<DropdownOption[]>((acc, meta) => {
    if (meta.action === ActionTypes.select) {
      return [...acc, meta.option!];
    }
    return acc.filter((val) => val.id !== meta.option?.id);
  }, currentSelected);

export const getSelectedValues = (
  selectedValues: TargetingValueOption[],
  meta: SelectOptionActionMeta<DropdownOption> | DeselectOptionActionMeta<DropdownOption>,
  selectedDimension: Nullable<DropdownOption>
): TargetingValueOption[] => {
  if (isClearAllOption(meta)) return selectedValues.filter(({ dimension }) => dimension !== selectedDimension?.id);

  if (meta.action === ActionTypes.select) {
    if (!selectedDimension?.id) return selectedValues;

    return [...selectedValues, { ...meta.option!, dimension: selectedDimension.id }];
  }

  return selectedValues.filter(({ id }) => id !== meta.option?.id);
};

export const hasValuesChanged = (newValues: TargetingValueOption[], selectedValues: TargetingValueOption[]): boolean =>
  newValues.length !== selectedValues.length || !newValues.every((v, i) => v.value === selectedValues[i].value);

export const getNoOptionsMessage = (searching: boolean, supportEmptySearch: boolean, searchTerm: string): string => {
  if (searching) return 'Loading';

  return supportEmptySearch || searchTerm ? 'No Options' : 'Start typing to find options';
};

export const getSupportedDimensionOptions = ({
  dropdownOptions,
  isAdEntity,
  lineItemPublishers,
  adTypes,
}: SupportedDimensionOptions): DropdownOption<string>[] => {
  if (!dropdownOptions?.length) return [];

  let dimensionExclusions: string[] = [];

  if (!isAdEntity) {
    dimensionExclusions = LINE_ITEM_DIMENSION_EXCLUSION_LIST;
  } else {
    const supportedPublisherOptions = getPotentialAdSupportedPublisherOptions({
      lineItemPublishers,
      adTypes,
    });

    if (supportedPublisherOptions.length <= 1) dimensionExclusions.push(CustomDimension.Publisher);
  }

  return dropdownOptions.reduce<DropdownOption<string>[]>((acc, dimension) => {
    if (dimension.id && !dimensionExclusions.includes(dimension.id)) {
      acc.push(dimension);
    }

    return acc;
  }, []);
};

const getPotentialAdSupportedPublisherOptions = ({
  lineItemPublishers,
  adTypes,
}: Pick<SupportedDimensionOptions, 'lineItemPublishers' | 'adTypes'>): DropdownOption<string>[] => {
  return getSupportedValuesOptions({
    dropdownOptions:
      lineItemPublishers?.map((lineItemPublisher) => {
        return {
          id: lineItemPublisher.id,
          label: lineItemPublisher.displayName,
          value: lineItemPublisher.displayName,
        };
      }) || [],
    selectedDimension: {
      id: CustomDimension.Publisher,
      label: CustomDimension.Publisher,
      value: CustomDimension.Publisher,
    },
    isAdEntity: true,
    lineItemPublishers,
    adTypes,
  });
};

export const isPublisherRestrictedByAdProductAndType = (publisherId: string, adTypes?: Maybe<AdType[]>): boolean => {
  if (!adTypes?.length) return false;

  for (const adType of adTypes) {
    const adTypeRestriction = AD_TYPE_PUBLISHER_RESTRICTIONS_MAP[adType];

    if (adTypeRestriction?.exclude?.includes(publisherId)) {
      return true;
    }
  }

  return false;
};

const sortOptionsByLabel = (options: DropdownOption<string>[]): DropdownOption<string>[] =>
  [...options].sort((a, b) => new Intl.Collator().compare(a.label ?? '', b.label ?? ''));

export const getSupportedValuesOptions = ({
  dropdownOptions,
  lineItemPublishers,
  isAdEntity,
  selectedDimension,
  adTypes,
}: SupportedValuesOptions): DropdownOption<string>[] => {
  if (!dropdownOptions?.length) return [];

  const isPublisherDimension = selectedDimension?.id === CustomDimension.Publisher;

  if (!isPublisherDimension) {
    return dropdownOptions;
  }

  // Publisher values are sorted alphabetically wherever they appear (Ad and Line
  // Item targeting). Non-publisher dimensions keep their incoming order.
  if (!isAdEntity) {
    return sortOptionsByLabel(dropdownOptions);
  }

  return sortOptionsByLabel(
    dropdownOptions.reduce<DropdownOption<string>[]>((acc, option) => {
      const isOptionInLineItemPublisherList = lineItemPublishers?.some(
        (publisher) => publisher.id === option.id && !isPublisherRestrictedByAdProductAndType(option.id, adTypes)
      );

      if (isOptionInLineItemPublisherList) {
        acc.push(option);
      }

      return acc;
    }, [])
  );
};

// disables targeting dropdown option and editing in targeting table
export function disableByDimensionId(dimensionId: string, editMode?: boolean): boolean {
  const found = DIMENSIONS_TO_DISABLE.some((d) => d === dimensionId);
  if (found) {
    return editMode === undefined;
  }

  return !!editMode;
}

export const isTargetingActionDisabled = ({
  selectedDimension,
  readonly,
  previouslySelectedValues,
  currentSelectedValues,
  adTypes,
  lineItemPublishers,
  lineItemCountryIds,
}: DisableTargetingActionsParams): boolean => {
  if (!currentSelectedValues?.length || readonly) {
    return true;
  }

  if (selectedDimension?.id === GeoDimension.Country) {
    const proposedSet = new Set(currentSelectedValues.map(({ id }) => id ?? ''));
    previouslySelectedValues.forEach(({ id }) => proposedSet.add(id ?? ''));
    const countrySet = new Set(lineItemCountryIds);
    if (proposedSet.size === countrySet.size && [...proposedSet].every((id) => countrySet.has(id))) {
      return true;
    }
  }

  if (selectedDimension?.id !== CustomDimension.Publisher) {
    return false;
  }

  const supportedPublisherOptions = getPotentialAdSupportedPublisherOptions({
    lineItemPublishers,
    adTypes,
  });

  return supportedPublisherOptions.every(({ id: supportedPublisherId }) =>
    previouslySelectedValues.find(({ id: selectedValueId }) => selectedValueId === supportedPublisherId)
  );
};
